/**
 * Route wrapper: opaque native auth gate until JWT session is valid.
 * Blueprint: docs/NATIVE_AUTH.md · docs/INTERNAL_INDEX.md
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import AuthView from './AuthView';
import {
  bootstrapAuthSession,
  clearStoredSession,
  logoutSession,
  readStoredSession,
} from '../../services/nativeAuth';
import { readTheme } from '../../utils/internalTheme';
import './AuthView.css';

const AuthContext = createContext({
  email: null,
  role: null,
  isAdmin: false,
  logout: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

/** @deprecated use useAuth */
export function useDomainAuth() {
  return useAuth();
}

export default function AuthGate({ children }) {
  const [status, setStatus] = useState('checking'); // checking | gated | ready
  const [email, setEmail] = useState(null);
  const [role, setRole] = useState(null);
  const [theme, setTheme] = useState(readTheme);

  useEffect(() => {
    const onTheme = (e) => {
      if (typeof e.detail === 'string') setTheme(e.detail);
      else setTheme(readTheme());
    };
    window.addEventListener('construx-internal-theme', onTheme);
    window.addEventListener('storage', onTheme);
    return () => {
      window.removeEventListener('construx-internal-theme', onTheme);
      window.removeEventListener('storage', onTheme);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const local = readStoredSession();
      if (!local) {
        if (!cancelled) {
          setEmail(null);
          setRole(null);
          setStatus('gated');
        }
        return;
      }

      try {
        const validated = await bootstrapAuthSession();
        if (cancelled) return;
        if (validated) {
          setEmail(validated.email);
          setRole(validated.role || 'member');
          setStatus('ready');
        } else {
          setEmail(null);
          setRole(null);
          setStatus('gated');
        }
      } catch {
        if (!cancelled) {
          setEmail(null);
          setRole(null);
          setStatus('gated');
        }
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const logout = useCallback(() => {
    const stored = readStoredSession();
    logoutSession(stored?.refreshToken, stored?.email).catch(() => {});
    clearStoredSession();
    setEmail(null);
    setRole(null);
    setStatus('gated');
  }, []);

  const isAdmin = role === 'admin';
  const ctx = useMemo(
    () => ({ email, role, isAdmin, logout }),
    [email, role, isAdmin, logout]
  );
  const themeAttr = theme === 'light' ? undefined : theme;

  if (status === 'checking') {
    return (
      <div
        className="domain-auth domain-auth--checking"
        data-hub-theme={themeAttr}
        aria-busy="true"
      >
        <Helmet>
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
          <link
            href="https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap"
            rel="stylesheet"
          />
        </Helmet>
        <div className="domain-auth__shell">
          <p className="domain-auth__checking">checking session</p>
        </div>
      </div>
    );
  }

  if (status === 'gated') {
    return (
      <AuthContext.Provider value={ctx}>
        <AuthView
          theme={theme}
          onAuthenticated={(next) => {
            setEmail(next?.email || null);
            setRole(next?.role || 'member');
            setStatus('ready');
          }}
        />
      </AuthContext.Provider>
    );
  }

  return <AuthContext.Provider value={ctx}>{children}</AuthContext.Provider>;
}
